// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { InformationCircleIcon, LockClosedIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { URL } from '../../service/Service';
import { useTranslation } from '../../i18n/LanguageContext';

const emptyForm = { name: '', nif: '', cid: '', site: '', notes: '', customerStatus: '' };

const EditClientModalUser = ({ customerID, partnerID, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(emptyForm);
  const [originalData, setOriginalData] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const jwtToken = sessionStorage.getItem('jwt');

  useEffect(() => {
    axios.get(`${URL}/api/partners/${partnerID}/customer/${customerID}`, { headers: { Authorization: `Bearer ${jwtToken}` } })
      .then((res) => {
        const loaded = { name: res.data.name || '', nif: res.data.nif || '', cid: res.data.customerID || '', site: res.data.site || '', notes: res.data.notes || '', customerStatus: res.data.customerStatus || 'PENDING' };
        setFormData(loaded);
        setOriginalData(loaded);
      })
      .catch((err) => setError(err.response?.data || err.message || 'Erro ao carregar cliente.'))
      .finally(() => setLoading(false));
  }, [partnerID, customerID, jwtToken]);

  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const normalize = (value) => String(value ?? '').trim();
  const hasChanges = useMemo(() => (
    normalize(formData.nif).replace(/\s/g, '') !== normalize(originalData.nif).replace(/\s/g, '') ||
    normalize(formData.site).toLowerCase().replace(/\/$/, '') !== normalize(originalData.site).toLowerCase().replace(/\/$/, '') ||
    normalize(formData.notes) !== normalize(originalData.notes)
  ), [formData, originalData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasChanges) {
      setError(t('editClient.noChanges'));
      return;
    }
    setSaving(true); setError(null); setSuccess(null);
    try {
      await axios.patch(`${URL}/api/partners/${partnerID}/customer/${customerID}`, formData, { headers: { Authorization: `Bearer ${jwtToken}` } });
      setSuccess(t('editClient.success'));
      setTimeout(() => window.location.reload(), 900);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data || err.message || 'Erro ao atualizar cliente.');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/60 p-4" onClick={(e) => e.target.classList.contains('modal-overlay') && onClose()}>
      <div className="modal-overlay absolute inset-0" />
      <div className="relative z-10 w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="border-b border-gray-100 px-6 py-5"><h2 className="text-2xl font-black text-gray-950">{t('editClient.title')}</h2><p className="mt-1 text-sm text-gray-500">{t('editClient.description')}</p></div>
        {loading ? <div className="p-8 text-center text-sm text-gray-500">{t('editClient.loading')}</div> : <form onSubmit={handleSubmit} className="space-y-5 p-6">
          <div className="flex items-start gap-3 rounded-2xl border border-teal-100 bg-teal-50/70 px-4 py-3.5 text-sm text-teal-950">
            <InformationCircleIcon className="mt-0.5 h-5 w-5 flex-none text-teal-600" aria-hidden="true" />
            <div>
              <p className="font-bold">{t('editClient.editableIntroTitle')}</p>
              <p className="mt-0.5 leading-5 text-teal-800">{t('editClient.editableIntro')}</p>
            </div>
          </div>
          {error && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">{error}</div>}
          {success && <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm font-semibold text-emerald-700">{success}</div>}
          <div className="grid gap-5 md:grid-cols-2">
            <label className="block">
              <span className="flex items-center justify-between gap-2 text-sm font-semibold text-gray-700"><span>{t('settings.name')}</span><span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400"><LockClosedIcon className="h-3.5 w-3.5" />{t('editClient.locked')}</span></span>
              <input name="name" value={formData.name} readOnly aria-readonly="true" className="mt-2 block w-full cursor-not-allowed rounded-xl border-gray-200 bg-gray-100 px-4 py-3 text-gray-500" />
            </label>
            <label className="block">
              <span className="flex items-center justify-between gap-2 text-sm font-semibold text-gray-700"><span>CID</span><span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400"><LockClosedIcon className="h-3.5 w-3.5" />{t('editClient.locked')}</span></span>
              <input name="cid" value={formData.cid} readOnly aria-readonly="true" className="mt-2 block w-full cursor-not-allowed rounded-xl border-gray-200 bg-gray-100 px-4 py-3 text-gray-500" /><span className="mt-1.5 block text-xs text-gray-500">{t('editClient.cidLocked')}</span>
            </label>
            <label className="block">
              <span className="flex items-center justify-between gap-2 text-sm font-semibold text-gray-700"><span>NIF</span><span className="inline-flex items-center gap-1 text-xs font-bold text-teal-700"><PencilSquareIcon className="h-3.5 w-3.5" />{t('editClient.editable')}</span></span>
              <input name="nif" value={formData.nif} onChange={handleChange} className="mt-2 block w-full rounded-xl border-teal-200 bg-white px-4 py-3 focus:border-green-cl focus:ring-green-cl" />
            </label>
            <label className="block md:col-span-2">
              <span className="flex items-center justify-between gap-2 text-sm font-semibold text-gray-700"><span>Website</span><span className="inline-flex items-center gap-1 text-xs font-bold text-teal-700"><PencilSquareIcon className="h-3.5 w-3.5" />{t('editClient.editable')}</span></span>
              <input name="site" value={formData.site} onChange={handleChange} placeholder="exemplo.pt" className="mt-2 block w-full rounded-xl border-teal-200 bg-white px-4 py-3 focus:border-green-cl focus:ring-green-cl" />
            </label>
            <label className="block md:col-span-2">
              <span className="flex items-center justify-between gap-2 text-sm font-semibold text-gray-700"><span>{t('clients.notes')}</span><span className="inline-flex items-center gap-1 text-xs font-bold text-teal-700"><PencilSquareIcon className="h-3.5 w-3.5" />{t('editClient.editable')}</span></span>
              <textarea name="notes" rows={4} value={formData.notes} onChange={handleChange} className="mt-2 block w-full rounded-xl border-teal-200 bg-white px-4 py-3 focus:border-green-cl focus:ring-green-cl" />
            </label>
          </div>
          <div className="flex justify-end gap-3 border-t border-gray-100 pt-5"><button type="button" onClick={onClose} className="rounded-xl border border-gray-300 px-5 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-50">{t('common.cancel')}</button><button disabled={saving || !hasChanges} title={!hasChanges ? t('editClient.noChangesTitle') : undefined} className="rounded-xl bg-green-cl px-5 py-2.5 text-sm font-bold text-white hover:bg-green-900 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 disabled:opacity-100">{saving ? t('editClient.saving') : t('editClient.save')}</button></div>
        </form>}
      </div>
    </div>
  );
};

export default EditClientModalUser;
